import { useState, useEffect } from 'react';
import { api } from '../api/client';

interface BriefingNote {
  who_is_this_person: string;
  what_they_need: string;
  primary_blocker_commentary: string;
  how_to_approach_them: string[];
  opening_question_for_session_1: string;
  red_flags: string[];
  cadence_notes: string;
  one_thing_to_watch: string;
}

interface CadenceRecommendation {
  session_rhythm: string;
  session_length: string;
  first_session: string;
  challenge_level: string;
  cadence_rationale: string;
}

type Answer = { value?: string | string[] | null; score?: number; follow_up?: string };

interface DiscoveryProfile {
  id: string;
  status: string;
  has_red_flags: boolean;
  defer_recommended: boolean;
  cadence: CadenceRecommendation | null;
  briefing_note: BriefingNote | null;
  trainer_observations: string | null;
  submitted_at: string | null;
  responses_core: Record<string, Record<string, Answer>> | null;
  responses_optional: Record<string, { value?: string | null }> | null;
  response_g: string | null;
}

// Short labels for the participant's own answers (questions are in pages/SeedbedDiscovery.tsx).
const QUESTION_LABELS: Record<string, string> = {
  A1: 'Where they are right now',
  A2: 'What led them to reach out',
  A3: 'Energy and headspace (1–10)',
  B1: 'What would be different',
  B2: 'One thing to understand',
  B3: 'Six months from now',
  C1: 'What gets in the way',
  C2: 'How long it has been in the way',
  D1: 'How full life is (1–10)',
  D2: 'Regular protected time',
  D3: 'Anything significant going on',
  E1: 'What helps when stuck',
  E2: 'Feedback preference',
  E3: 'What stops new things sticking',
  E4: 'Clearest thinking time',
  F1: 'Personal circumstances',
  F2: 'People who might be affected',
  F3: 'Topics to handle with care',
  G1: 'Anything else',
};

function formatAnswer(a: Answer | undefined): string {
  if (!a) return '';
  const parts: string[] = [];
  if (typeof a.score === 'number') parts.push(`${a.score}/10`);
  if (Array.isArray(a.value)) parts.push(a.value.join(', '));
  else if (a.value) parts.push(String(a.value));
  if (a.follow_up) parts.push(`(${a.follow_up})`);
  return parts.join(' ');
}

function AnswerList({ entries }: { entries: [string, string][] }) {
  return (
    <dl className="space-y-2">
      {entries.map(([id, text]) => (
        <div key={id}>
          <dt className="text-xs font-semibold text-slate-600">{QUESTION_LABELS[id] || id}</dt>
          <dd className="text-sm text-[#1A1A2E] leading-relaxed whitespace-pre-line">{text}</dd>
        </div>
      ))}
    </dl>
  );
}

const RHYTHM_LABELS: Record<string, string> = {
  standard_fortnightly: 'Standard Fortnightly',
  fortnightly_with_checkins: 'Fortnightly + Check-ins',
  consider_weekly: 'Consider Weekly',
  defer_discussion: 'Defer — Discuss Timing',
};

const LENGTH_LABELS: Record<string, string> = {
  standard_60: '60 minutes',
  extended_first_75: '75 min (Session 1)',
  reduced_45: '45 minutes',
};

const SESSION_LABELS: Record<string, string> = {
  standard: 'Standard',
  extended_discovery: 'Extended Discovery',
  pre_call_advised: 'Pre-call Advised',
};

const CHALLENGE_LABELS: Record<string, { label: string; color: string }> = {
  foundation_first: { label: 'Foundation First', color: 'bg-blue-100 text-blue-700' },
  standard_seedbed: { label: 'Standard Seedbed', color: 'bg-green-100 text-green-700' },
  direct_challenge: { label: 'Direct Challenge', color: 'bg-amber-100 text-amber-700' },
};

export function SeedDiscoveryPanel({ candidateId }: { candidateId: string }) {
  const [data, setData] = useState<DiscoveryProfile | null>(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  const [observations, setObservations] = useState('');
  const [savingObs, setSavingObs] = useState(false);

  useEffect(() => {
    api.get<DiscoveryProfile>(`/seedbed/discovery/${candidateId}`)
      .then((d) => { setData(d); setObservations(d.trainer_observations || ''); })
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [candidateId]);

  const saveObservations = async () => {
    if (!data) return;
    setSavingObs(true);
    try {
      // The observations endpoint is keyed by the discovery ID (not the candidate ID).
      await api.patch(`/seedbed/discovery/${data.id}/observations`, { trainer_observations: observations });
    } catch { /* silent */ }
    setSavingObs(false);
  };

  if (loading) return <div className="bg-white rounded-xl p-4 border border-slate-100 animate-pulse"><div className="h-4 bg-slate-200 rounded w-48" /></div>;

  if (!data) {
    return (
      <div className="bg-white rounded-xl p-5 border border-slate-100">
        <div className="flex items-center justify-between">
          <h3 className="font-display font-bold text-[#1B4332] text-sm">Seedbed Discovery</h3>
          <span className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-500 font-medium">Not started</span>
        </div>
        <p className="text-xs text-slate-400 mt-2">Candidate has not yet completed the Discovery Tool.</p>
      </div>
    );
  }

  const statusBadge = data.status === 'complete'
    ? 'bg-green-100 text-green-700'
    : data.status === 'processing'
      ? 'bg-amber-100 text-amber-700'
      : data.status === 'failed'
        ? 'bg-red-100 text-red-600'
        : 'bg-slate-100 text-slate-500';

  return (
    <div className="bg-white rounded-xl border border-slate-100 overflow-hidden">
      {/* Header — always visible */}
      <button onClick={() => setExpanded(!expanded)} className="w-full p-5 flex items-center justify-between hover:bg-slate-50 transition-colors">
        <div className="flex items-center gap-3">
          <h3 className="font-display font-bold text-[#1B4332] text-sm">Seedbed Discovery</h3>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${statusBadge}`}>
            {data.status === 'complete' ? 'Complete' : data.status === 'processing' ? 'Generating...' : data.status}
          </span>
          {data.has_red_flags && <span className="text-xs px-2 py-0.5 rounded bg-red-100 text-red-600 font-medium">Flags</span>}
        </div>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${expanded ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="px-5 pb-5 space-y-4">
          {/* Red flags */}
          {data.has_red_flags && data.briefing_note?.red_flags && data.briefing_note.red_flags.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-xs font-bold text-red-700 mb-2">Requires attention before Session 1</p>
              {data.briefing_note.red_flags.map((flag, i) => (
                <p key={i} className="text-sm text-red-600">{flag}</p>
              ))}
            </div>
          )}

          {/* Deferral */}
          {data.defer_recommended && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-700 font-medium">A conversation about timing may be appropriate before beginning.</p>
            </div>
          )}

          {data.briefing_note ? (
            <>
              {/* One thing to watch */}
              <div className="bg-[#D4A017]/10 border border-[#D4A017]/20 rounded-lg p-4">
                <p className="text-xs font-bold text-[#D4A017] mb-1">Hold this throughout the programme:</p>
                <p className="text-sm text-[#1A1A2E] font-medium">{data.briefing_note.one_thing_to_watch}</p>
              </div>

              {/* Who is this person */}
              <div className="border-l-4 border-[#1B4332] bg-slate-50 rounded-r-lg p-4">
                <p className="text-xs font-bold text-[#1B4332] mb-2">Portrait</p>
                <p className="text-sm text-[#1A1A2E] leading-relaxed whitespace-pre-line">{data.briefing_note.who_is_this_person}</p>
              </div>

              {/* What they need */}
              <div className="bg-[#FAFAF7] rounded-lg p-4">
                <p className="text-xs font-bold text-[#1B4332] mb-2">What they need from Seedbed</p>
                <p className="text-sm text-[#1A1A2E] leading-relaxed whitespace-pre-line">{data.briefing_note.what_they_need}</p>
              </div>

              {/* Primary blocker */}
              <div className="border-l-4 border-amber-400 bg-amber-50/50 rounded-r-lg p-4">
                <p className="text-xs font-bold text-amber-700 mb-2">On the primary blocker</p>
                <p className="text-sm text-[#1A1A2E] leading-relaxed">{data.briefing_note.primary_blocker_commentary}</p>
              </div>

              {/* How to approach */}
              <div className="bg-[#1B4332]/5 rounded-lg p-4">
                <p className="text-xs font-bold text-[#1B4332] mb-2">Coaching style guidance</p>
                <ul className="space-y-1.5">
                  {data.briefing_note.how_to_approach_them.map((bullet, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-[#1A1A2E]">
                      <span className="text-[#52B788] mt-0.5 flex-shrink-0">&bull;</span>{bullet}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Opening question */}
              <div className="bg-[#D4A017]/10 rounded-lg p-4">
                <p className="text-xs font-bold text-[#D4A017] mb-2">Open Session 1 with</p>
                <p className="text-base text-[#1A1A2E] italic font-medium leading-relaxed">"{data.briefing_note.opening_question_for_session_1}"</p>
              </div>

              {/* Cadence notes */}
              {data.briefing_note.cadence_notes && (
                <div>
                  <p className="text-xs font-bold text-[#1B4332] mb-1">Cadence observations</p>
                  <p className="text-sm text-slate-600 leading-relaxed">{data.briefing_note.cadence_notes}</p>
                </div>
              )}
            </>
          ) : data.status === 'processing' ? (
            <div className="flex items-center gap-3 py-4">
              <div className="w-4 h-4 border-2 border-[#1B4332]/30 border-t-[#1B4332] rounded-full animate-spin" />
              <p className="text-sm text-slate-500">Generating trainer briefing note...</p>
            </div>
          ) : data.status === 'failed' ? (
            <div className="bg-slate-50 rounded-lg p-4">
              <p className="text-sm text-slate-500">Briefing note could not be generated — raw responses available below.</p>
            </div>
          ) : null}

          {/* Cadence recommendation card */}
          {data.cadence && (
            <div className="bg-white border border-slate-200 rounded-lg p-4">
              <p className="text-xs font-bold text-[#1B4332] mb-3">Cadence Recommendation</p>
              <div className="grid grid-cols-2 gap-3 text-xs mb-3">
                <div>
                  <p className="text-slate-400 font-medium">Session Rhythm</p>
                  <p className="text-[#1A1A2E] font-semibold">{RHYTHM_LABELS[data.cadence.session_rhythm] || data.cadence.session_rhythm}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-medium">Session Length</p>
                  <p className="text-[#1A1A2E] font-semibold">{LENGTH_LABELS[data.cadence.session_length] || data.cadence.session_length}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-medium">First Session</p>
                  <p className="text-[#1A1A2E] font-semibold">{SESSION_LABELS[data.cadence.first_session] || data.cadence.first_session}</p>
                </div>
                <div>
                  <p className="text-slate-400 font-medium">Challenge Level</p>
                  <span className={`inline-block px-2 py-0.5 rounded text-xs font-semibold ${CHALLENGE_LABELS[data.cadence.challenge_level]?.color || 'bg-slate-100 text-slate-600'}`}>
                    {CHALLENGE_LABELS[data.cadence.challenge_level]?.label || data.cadence.challenge_level}
                  </span>
                </div>
              </div>
              <p className="text-xs text-slate-500 leading-relaxed">{data.cadence.cadence_rationale}</p>
            </div>
          )}

          {/* Section F: shared with the trainer only, never sent to the AI briefing */}
          {(() => {
            const f = Object.entries(data.responses_optional || {})
              .filter(([, a]) => a?.value)
              .map(([id, a]) => [id, String(a.value)] as [string, string]);
            if (!f.length) return null;
            return (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <p className="text-xs font-bold text-[#1B4332] mb-1">Personal context: shared with you only</p>
                <p className="text-xs text-slate-600 mb-3">The participant chose to share this. It is not included in the AI briefing above. Treat it with care.</p>
                <AnswerList entries={f} />
              </div>
            );
          })()}

          {/* The participant's own answers (sections A–E and G) */}
          {(data.responses_core || data.response_g) && (
            <details className="border border-slate-200 rounded-lg p-4" open={data.status === 'failed'}>
              <summary className="text-xs font-bold text-[#1B4332] cursor-pointer">Their own answers</summary>
              <div className="mt-3">
                <AnswerList
                  entries={[
                    ...Object.values(data.responses_core || {}).flatMap((section) =>
                      Object.entries(section || {})
                        .map(([id, a]) => [id, formatAnswer(a)] as [string, string])
                        .filter(([, text]) => text),
                    ),
                    ...(data.response_g ? [['G1', data.response_g] as [string, string]] : []),
                  ].sort(([a], [b]) => a.localeCompare(b))}
                />
              </div>
            </details>
          )}

          {/* Trainer observations */}
          <div>
            <p className="text-xs font-bold text-[#1B4332] mb-1">Your observations</p>
            <p className="text-[10px] text-slate-400 mb-2">Not shared with candidate. Auto-saves on blur.</p>
            <textarea
              value={observations}
              onChange={(e) => setObservations(e.target.value)}
              onBlur={saveObservations}
              rows={3}
              placeholder="Add your own notes after reading the briefing or after a pre-session call..."
              className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent outline-none resize-none"
            />
            {savingObs && <p className="text-[10px] text-green-500 mt-1">Saving...</p>}
          </div>
        </div>
      )}
    </div>
  );
}
